import { Token, PaymentRequestCompleteStatus } from '@stripe/stripe-js';
import axios, { AxiosRequestConfig } from 'axios';
import { isOrderAllowedInNewYork } from 'components/Checkout/checkoutHelpers';
import Cookies from 'js-cookie';
import ewAnalytics from 'utils/analytics';
import { getOrder, getAuthToken, getLineItems } from 'utils/cartHelpers';
import { getShippingMethodId, getShippingMethodInfo } from 'utils/cartHelpers';
import { ANALYTICS } from 'utils/constants/analytics';
import { CookieNames } from 'utils/constants/cookies';
import { API_ROOT } from 'utils/constants/urls';
import { snakeToTitle } from 'utils/helpers/index';
import { ShippingAddressType, StripeCardType, LineItem } from 'utils/types';

import {
  dataLayerCaptureUserAttributes,
  dataLayerReceiveMarketing,
} from '../../components/Checkout/AnalyticsHelpers';

const processStripeOrder = async (
  paymentData: any,
  token: Token,
  label: string,
) => {
  if (typeof window === 'undefined') return 'fail';

  const order = JSON.parse(await getOrder());
  let orderStatus: PaymentRequestCompleteStatus = 'success';
  const orderId = order.id;
  const registrationToken = order.registration_token;
  const { payerEmail, shippingAddress } = paymentData;
  const { addressLine, city, country, postalCode, region, recipient } =
    shippingAddress as ShippingAddressType;
  const { card, id } = token;
  const {
    name,
    address_city,
    address_country,
    address_zip,
    address_state,
    address_line1,
    address_line2,
  } = card as StripeCardType;

  const splitShipName = recipient.split(' ');

  const shipping = {
    first_name: splitShipName.slice(0, -1).join(' '),
    last_name: splitShipName[splitShipName.length - 1],
    street1: addressLine[0],
    street2: addressLine[1],
    zipcode: postalCode,
    state: region,
    city,
    country: country.toLocaleUpperCase(),
    type: 'shipping',
  };

  const splitBillName = name.split(' ');

  const billing = {
    first_name: splitBillName.slice(0, -1).join(' '),
    last_name: splitBillName[splitBillName.length - 1],
    street1: address_line1,
    street2: address_line2,
    zipcode: address_zip,
    state: address_state,
    city: address_city,
    country: address_country.toLocaleUpperCase(),
    type: 'billing',
  };

  // Build cookie metadata as parameters for iterable
  const ipCookie = Cookies.get(CookieNames.UserIp);
  const campaignId = Cookies.get(CookieNames.IterableEmailCampaignId);
  const templateId = Cookies.get(CookieNames.IterableTemplateId);
  const messageId = Cookies.get(CookieNames.IterableMessageId);
  const cookieMetaData = {
    ipCookie,
    campaignId,
    templateId,
    messageId,
  };

  const restrictedStates = ['NY'];

  const trackProcessingError = () => {
    if (typeof window === 'undefined') return;

    if (order) {
      // Log conversion in Mixpanel
      // @ts-ignore
      window.mixpanel &&
        // @ts-ignore
        window.mixpanel.track(ANALYTICS.EVENTS.RESTRICTED_STATE_ERROR, {
          label: 'DLS Cart Restricted State Error',
          id: parseInt(orderId),
          revenue: order.total,
        });
    }
  };

  try {
    if (
      restrictedStates.indexOf(region) !== -1 &&
      !isOrderAllowedInNewYork(order)
    ) {
      throw `For regulatory reasons, our tests are not permitted for use in NY with the exception of COVID-19.`;
    }

    // Shipping address validation
    const shipping_errors = Object.keys(shipping).reduce(
      (errors: string[], key: string) => {
        //@ts-ignore
        if (!shipping[key] && key !== 'street2') {
          errors.push(`${snakeToTitle(key)} cannot be empty. `);
        }

        return errors;
      },
      [],
    );

    if (shipping_errors.length > 0) {
      orderStatus = 'invalid_shipping_address';
      throw `Your payment was not completed successfully.
      Please update the shipping address information in your wallet to complete your purchase, or
      click the "Proceed to Checkout" button to enter your information manually.`;
    }

    const newCartExp =
      // @ts-ignore
      (typeof window !== 'undefined' && window.newCartExp) || '';

    const lineItemUrl = `${
      // @ts-ignore
      (typeof window !== 'undefined' && window.apiRoot) || API_ROOT
      // @ts-ignore
    }/v2/orders/${orderId}/process_order?nc=${newCartExp}&ep=true`; // TODO: remove these attrs after cart test

    const authToken = await getAuthToken();

    const orderData = {
      email: payerEmail.trim(),
      terms: '1',
      marketing: '0',
      charity_id: '0',
      payment: {
        stripe_token: id,
        payment_method: 'stripe',
      },
      ship_address: shipping,
      bill_address: billing,
      billing_agreement_id: null,
      cookie_data: cookieMetaData,
    };

    const requestOpts: AxiosRequestConfig = {
      data: orderData,
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
      method: 'PUT',
      url: lineItemUrl,
    };

    const trackConversion = () => {
      if (typeof window !== 'undefined') {
        const code =
          order.promotions &&
          order.promotions.length > 0 &&
          order.promotions[0].code;

        const productsOrig = getLineItems();
        const products: any = [];

        productsOrig.length > 0 &&
          productsOrig.forEach(
            (product: {
              product_name: any;
              product_id: any;
              base_price: string;
              variant_sku: any;
              quantity: any;
              is_subscription: any;
              subscription_frequency: any;
            }) => {
              const transformedProduct = {
                name: product.product_name,
                id: product.product_id,
                price: parseFloat(product.base_price),
                variant: product.variant_sku,
                quantity: product.quantity,
                is_subscription: product.is_subscription,
                subscription_frequency: product.subscription_frequency,
              };

              products.push(transformedProduct);
            },
          );

        if (order) {
          const shippingMethodId = getShippingMethodId();
          const { shippingType, shippingCost } =
            getShippingMethodInfo(shippingMethodId);

          ewAnalytics.track({
            event: ANALYTICS.EVENTS.PURCHASE,
            data: {
              label: label,
              id: parseInt(orderId),
              revenue: order.total,
              coupon: code,
              discount: parseFloat(order.promo_total),
              shipping_method_id: shippingMethodId,
              shipping_type: shippingType,
              shipping_cost: shippingCost,
            },
          });

          // Log conversion in GTM
          // @ts-ignore
          window.dataLayer &&
            // @ts-ignore
            window.dataLayer.push({
              event: 'purchase',
              ecommerce: {
                purchase: {
                  actionField: {
                    id: parseInt(orderId),
                    revenue: order.total,
                    coupon: code,
                    discount: parseFloat(order.promo_total),
                  },
                  products,
                },
              },
              userEmail: payerEmail.trim(),
            });

          // cartCheckout GTM event
          dataLayerCaptureUserAttributes({
            // @ts-ignore
            ship_address: {
              first_name: shipping.first_name,
              last_name: shipping.last_name,
              city: shipping.city,
              state: shipping.state,
              phone: '',
            },
            email: orderData.email,
          });
          // marketing GTM event
          dataLayerReceiveMarketing(false);
        }
      }
    };

    const trackPaymentError = () => {
      if (typeof window !== 'undefined') {
        if (order) {
          // Log conversion in Mixpanel
          // @ts-ignore
          window.mixpanel &&
            // @ts-ignore
            window.mixpanel.track(ANALYTICS.EVENTS.PURCHASE_ERROR, {
              label: 'DLS Cart Express Pay Purchase Error',
              id: parseInt(orderId),
              revenue: order.total,
            });
        }
      }
    };

    await axios(requestOpts)
      .then(() => {
        trackConversion();
      })
      .then(() => {
        if (typeof window !== 'undefined') {
          window.location.replace(
            `/confirmation?orderId=${orderId}&registration_token=${registrationToken}&referral=true`,
          );
        }
      })
      .catch((error) => {
        orderStatus = 'fail';
        trackPaymentError();

        if (error.request) {
          // The request was made but no response was received
          // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
          // http.ClientRequest in node.js
          const errors = (typeof error.request === 'string' && [
            error.request,
          ]) || [error.message];

          const event = new CustomEvent('onCartTrigger', {
            detail: { error: { type: 'cartError', errors } },
          });

          typeof window !== 'undefined' && window.dispatchEvent(event);
        }
      });
  } catch (error) {
    if (typeof window === 'undefined') return 'fail';

    trackProcessingError();

    let errors = error;
    if (typeof error === 'string') errors = [error];

    const event = new CustomEvent('onCartTrigger', {
      detail: { error: { type: 'cartError', errors } },
    });

    if (typeof window === 'undefined') return 'fail';
    window.dispatchEvent(event);
  }

  return orderStatus;
};

const stripePublicKey: string =
  // @ts-ignore
  (typeof window !== 'undefined' && window.stripePublicKey) ||
  process.env.STRIPE_PUBLIC_KEY;

// Enable Express payments on normal items, subscription, and single membership orders
// Disable Express payments on orders with both a subscription/membership order + a normal item
const hasSubscriptionItemInCart = (
  lineItems: LineItem[] | undefined,
): boolean => !!lineItems?.some((li) => li.is_subscription);

// Has at least one lab visit item in cart
const hasLabVisitItemInCart = (lineItems: LineItem[] | undefined): boolean =>
  !!lineItems?.some((item) => item.is_lab_visit);

export {
  stripePublicKey,
  processStripeOrder,
  hasSubscriptionItemInCart,
  hasLabVisitItemInCart,
};
